import type { AxiosInstance } from 'axios';

/**
 * HTTP capability for the NDJSON streaming endpoints. The host builds the tenant
 * URL + attaches the bearer *inside* this fn; callers pass only a `/api/...` path.
 * Mirrors `FtueHostAdapter.authedFetch`.
 */
export type AuthedFetch = (path: string, init?: RequestInit) => Promise<Response>;

const EXTRACT_RULES_ENDPOINT = '/api/v2/onboarding/extract-rules';
const EXTRACT_RULES_FROM_PLAYBOOK_DOC_ENDPOINT =
  '/api/v2/onboarding/extract-rules-from-playbook-doc';
const SAVE_RULES_ENDPOINT = '/api/v2/onboarding/save-rules';
const CLONE_PLAYBOOK_ENDPOINT = '/api/v2/onboarding/clone-playbook';
const SEARCH_RULES_ENDPOINT = '/api/v2/onboarding/search-rules';
const MATCH_EXTRACTED_RULES_ENDPOINT = '/api/v2/onboarding/match-extracted-rules';
const MODELS_ENDPOINT = '/api/trainingmodels';
const TRAINING_RULE_SECTIONS_ENDPOINT = '/api/training/rule/sections';

export interface SuggestedMatch {
  ruleId: string;
  name: string;
  description: string;
  playbookId: string;
  playbookName: string;
  confidence: number;
  sampleEdit?: { before: string; after: string };
  /** True when the matching rule belongs to the playbook being augmented. */
  samePlaybook?: boolean;
}

export interface ExtractedRule {
  id: string;
  name: string;
  description: string;
  sourceText?: string;
  // snake_case variants returned directly from the Python extraction endpoint
  source_text?: string;
  originalText?: string;
  original_text?: string;
  editedText?: string;
  edited_text?: string;
  editCategory?: string;
  edit_category?: string;
  contextBefore?: string;
  context_before?: string;
  contextFollowing?: string;
  context_following?: string;
  confidence: 'high' | 'medium' | 'low';
  accepted: boolean;
  isFallback: boolean;
  sampleEdits?: Array<{ before: string; after: string }>;
  suggestedMatches?: Array<SuggestedMatch>;
  /** Set when the user chooses to add training to an existing same-playbook rule. */
  existingRuleId?: string;
  /** snake_case variant returned by the extraction API when the rule matched an existing one. */
  existing_rule_id?: string;
}

/** Derive a before/after sample for an extracted rule if the data is present. */
export function extractedRuleSampleEdits(
  rule: ExtractedRule
): Array<{ before: string; after: string; contextBefore?: string; contextFollowing?: string }> {
  const editCategory = rule.edit_category ?? rule.editCategory ?? '';
  const contextBefore = rule.context_before ?? rule.contextBefore ?? '';
  const contextFollowing = rule.context_following ?? rule.contextFollowing ?? '';
  const context = contextBefore || contextFollowing || (rule.source_text ?? rule.sourceText ?? '');
  const insertText = rule.edited_text ?? rule.editedText ?? '';
  const originalText = rule.original_text ?? rule.originalText ?? '';

  // For insert rules: show context as-is, then the inserted text appended in green
  if (editCategory === 'insert' && insertText) {
    const before = context;
    const after = context ? `${context} ${insertText}` : insertText;
    return [{ before, after, contextBefore, contextFollowing }];
  }

  // For revise/delete rules: standard before/after diff
  const before = originalText || context;
  const after = editCategory === 'delete' ? '' : insertText;
  if (before && (after || editCategory === 'delete') && before !== after) {
    return [{ before, after }];
  }
  return rule.sampleEdits ?? [];
}

export interface ExtractedSection {
  id: string;
  name: string;
  rules: Array<ExtractedRule>;
}

export interface PlaybookModel {
  _id: string;
  name: string;
  contractType?: string;
  stage: string;
  dateModified: string;
}

export interface PlaybookRule {
  _id: string;
  name: string;
  description: string;
  sections: Array<string>;
  trainTypes?: Array<string>;
  sampleEdit?: { before: string; after: string; contextBefore?: string; contextFollowing?: string };
  sampleEdits?: Array<{
    before: string;
    after: string;
    contextBefore?: string;
    contextFollowing?: string;
  }>;
  protectionConfig?: { enabled?: boolean };
  protection_config?: { enabled?: boolean };
}

export interface PlaybookSection {
  _id: string;
  name: string;
  contractType?: string;
}

export async function getModels(bbAxios: AxiosInstance): Promise<Array<PlaybookModel>> {
  const { data } = await bbAxios.get<Array<PlaybookModel>>(MODELS_ENDPOINT);
  return data;
}

interface ModelWithRules {
  SUCCESS: boolean;
  data: {
    trainingRules: Array<
      PlaybookRule & {
        examples?: Array<{
          sentence: string;
          edited_sentence: string;
          train_type: string;
          context_before?: string;
          context_following?: string;
        }>;
      }
    >;
  };
}

export async function getPlaybookRules(
  bbAxios: AxiosInstance,
  playbookId: string
): Promise<Array<PlaybookRule>> {
  const { data } = await bbAxios.get<ModelWithRules>(
    `${MODELS_ENDPOINT}/${playbookId}?includeExamples=true`
  );
  const mapped = (data.data.trainingRules ?? []).map((rule) => {
    const isFxi = (rule.trainTypes ?? []).some((t) =>
      ['fsi', 'fpi', 'fci'].includes(String(t).toLowerCase())
    );
    // Insert-type examples (FSI/FPI/FCI) legitimately have an empty `sentence` — there's no
    // existing text being replaced, only an anchor/context the insert lands near. Prefer the
    // example's own train_type; fall back to the rule-level isFxi when it's absent.
    const isInsertExample = (ex: { train_type: string }) =>
      ex.train_type ? ['fsi', 'fpi', 'fci'].includes(String(ex.train_type).toLowerCase()) : isFxi;

    const sampleEdits = (rule.examples ?? [])
      .filter((ex) =>
        isInsertExample(ex)
          ? Boolean(ex.context_before || ex.context_following || ex.sentence) && ex.edited_sentence
          : // edited_sentence is checked for string-ness, not truthiness — an empty string is the
            // correct "after" value for deletion-type rules (FSD/FPD), not missing data.
            ex.sentence && typeof ex.edited_sentence === 'string'
      )
      .slice(0, 5)
      .map((ex) => {
        if (!isInsertExample(ex)) {
          return { before: ex.sentence, after: ex.edited_sentence };
        }
        // Insert-type: there's nothing being replaced, so before/after are built from the
        // surrounding context plus the inserted text — mirrors extractedRuleSampleEdits'
        // handling of the analogous extraction-time "insert" edit category.
        const contextBefore = ex.context_before || '';
        const contextFollowing = ex.context_following || '';
        const context = contextBefore || contextFollowing || ex.sentence || '';
        return {
          before: context,
          after: context ? `${context} ${ex.edited_sentence}` : ex.edited_sentence,
          contextBefore,
          contextFollowing,
        };
      });
    // The API populates sections as full objects { _id, name, ... }; normalize to string IDs
    // so that section-membership checks in the UI work correctly.
    const sections = (rule.sections ?? []).map((s: string | { _id: string }) =>
      typeof s === 'string' ? s : s._id
    );
    return {
      ...rule,
      sections,
      sampleEdits,
      sampleEdit: sampleEdits[0],
    };
  });
  // Deduplicate by _id — the trainingRules array on a playbook document can
  // contain the same ObjectId more than once, which causes Mongoose to return
  // the same rule document twice and the UI to render it twice.
  return [...new Map(mapped.map((r) => [r._id, r])).values()];
}

export async function getPlaybookSections(
  bbAxios: AxiosInstance,
  playbookId: string
): Promise<Array<PlaybookSection>> {
  const { data } = await bbAxios.get<Array<PlaybookSection>>(
    `${TRAINING_RULE_SECTIONS_ENDPOINT}/${playbookId}`
  );
  return data;
}

export interface ExtractionProgress {
  file: string;
  rules_extracted: number;
  rules_from_file: number;
  files_processed: number;
  total_files: number;
  message?: string;
  pdf_stage?: string;
  done?: number;
  total?: number;
  error?: string;
  /** Present on heartbeat-derived updates -- seconds since the stream opened. */
  elapsedSeconds?: number;
  /** Edits analyzed so far / total, from the LLM-enrichment phase (post-
   * clustering edit count) -- a real per-file progress measure, unlike
   * files_processed/total_files which is nearly useless for the common
   * single-file upload case. */
  analyzed_edits?: number;
  total_edits?: number;
  /** Chunks completed so far / total, from policy-document chunked extraction --
   * same rationale as analyzed_edits/total_edits, for the Strategy C (playbook
   * doc) path instead of the contract LLM-enrichment path. */
  chunks_completed?: number;
  total_chunks?: number;
}

/** Human-facing status line for an extraction/analysis progress update. */
export function extractionProgressMessage(progress: ExtractionProgress | null): string {
  if (!progress) {
    return 'Reading document(s)… this can take a few minutes for larger files.';
  }
  const statusText = progress.message || progress.file || 'Extracting rules…';
  if (progress.rules_extracted > 0) {
    return `${progress.rules_extracted} rule${progress.rules_extracted !== 1 ? 's' : ''} found so far — ${statusText}`;
  }
  return statusText;
}

/** Bounds a completed/total ratio into a [floor, floor+span] percent range. */
function boundedRatioPercent(current: number, total: number, floor: number, span: number): number {
  return Math.min(floor + span, floor + (current / total) * span);
}

/**
 * Percent-complete for the progress bar. Phases, checked most-recent-first
 * since a job can pass through more than one (contract LLM enrichment, then
 * policy-doc chunked extraction) and the later phase's counts should win:
 *
 * 1. Policy-doc chunked extraction, once total_chunks is known -- each chunk
 *    is a real, meaningful-even-for-a-single-doc checkpoint.
 * 2. Contract LLM enrichment, once total_edits is known (post-clustering
 *    edit count) -- same rationale, for the contract-file path.
 * 3. Parsing/alignment, before either count is known -- creep forward with
 *    elapsed time so the bar visibly moves instead of sitting static, capped
 *    low since there's no real signal yet.
 *
 * Phases 1 and 2 hold just under their ceiling once their count maxes out,
 * rather than claiming 100% -- there's still real work after the last chunk
 * or edit (clustering, dedup, classification, merging) with no numeric
 * signal of its own.
 */
export function extractionPercentComplete(progress: ExtractionProgress | null): number {
  const totalChunks = progress?.total_chunks ?? 0;
  if (totalChunks > 0) {
    return boundedRatioPercent(progress?.chunks_completed ?? 0, totalChunks, 15, 70); // 15-85%
  }

  const totalEdits = progress?.total_edits ?? 0;
  if (totalEdits > 0) {
    return boundedRatioPercent(progress?.analyzed_edits ?? 0, totalEdits, 15, 70); // 15-85%
  }

  const totalFiles = progress?.total_files ?? 0;
  const filesProcessed = progress?.files_processed ?? 0;
  const fileBasedPercent = totalFiles > 0 ? (filesProcessed / totalFiles) * 100 : 0;
  const elapsedSeconds = progress?.elapsedSeconds ?? 0;
  const timeBasedPercent = Math.min(15, elapsedSeconds / 4);
  return Math.max(fileBasedPercent, timeBasedPercent);
}

/**
 * current/total pair for the "N of TOTAL items" line under the progress bar.
 * Same most-recent-first priority as extractionPercentComplete: chunk counts,
 * then edit counts, then file counts (nearly always static 0-of-1 / 1-of-1
 * for the common single-file upload).
 */
export function extractionCurrentTotal(progress: ExtractionProgress | null): {
  current: number;
  total: number;
} {
  const totalChunks = progress?.total_chunks ?? 0;
  if (totalChunks > 0) {
    return { current: progress?.chunks_completed ?? 0, total: totalChunks };
  }

  const totalEdits = progress?.total_edits ?? 0;
  if (totalEdits > 0) {
    return { current: progress?.analyzed_edits ?? 0, total: totalEdits };
  }
  return { current: progress?.files_processed ?? 0, total: progress?.total_files ?? 0 };
}

function createStreamHttpError(
  prefix: string,
  response: Response
): Error & {
  isAuthError?: boolean;
  status?: number;
} {
  const err = new Error(`${prefix}: ${response.status} ${response.statusText}`) as Error & {
    isAuthError?: boolean;
    status?: number;
  };
  err.status = response.status;
  if (response.status === 401) {
    err.isAuthError = true;
  }
  return err;
}

export async function extractRulesFromContracts(
  bbAxios: AxiosInstance,
  {
    files,
    paperType,
    contractType,
    playbookId,
  }: {
    files: Array<File>;
    paperType: 'first_party' | 'third_party';
    contractType: string;
    playbookId?: string;
  }
): Promise<Array<ExtractedSection>> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  formData.append('paper_type', paperType);
  formData.append('contract_type', contractType);
  if (playbookId) {
    formData.append('playbook_id', playbookId);
  }
  const { data } = await bbAxios.post<{ docs: Array<ExtractedSection> }>(
    EXTRACT_RULES_ENDPOINT,
    formData,
    {
      timeout: 600_000,
    }
  );
  return data.docs;
}

/**
 * Streaming variant of extractRulesFromContracts. Calls the /stream endpoint and
 * fires onProgress after each file is processed so the UI can show live feedback.
 * Resolves to the final merged sections from the "complete" event.
 */
export async function extractRulesFromContractsStream(
  authedFetch: AuthedFetch,
  {
    files,
    paperType,
    contractType,
    playbookId,
  }: {
    files: Array<File>;
    paperType: 'first_party' | 'third_party';
    contractType: string;
    playbookId?: string;
  },
  onProgress: (progress: ExtractionProgress) => void,
  onJobCreated?: (jobId: string) => void
): Promise<ExtractionResult> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  formData.append('paper_type', paperType);
  formData.append('contract_type', contractType);
  if (playbookId) {
    formData.append('playbook_id', playbookId);
  }

  const response = await authedFetch(`${EXTRACT_RULES_ENDPOINT}/stream`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw createStreamHttpError('Extraction failed', response);
  }
  if (!response.body) {
    throw new Error('No response body from stream');
  }

  // Backstop in case an intermediate proxy ever strips/reorders the
  // `job_created` NDJSON line -- Node sets this header before piping the
  // stream through (see onboarding-router.mjs's /extract-rules/stream proxy).
  const headerJobId = response.headers.get('X-Async-Job-Id');
  if (headerJobId) {
    onJobCreated?.(headerJobId);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  type StreamEvent =
    | ({ type: 'progress' } & ExtractionProgress)
    | {
        type: 'status';
        message: string;
        files_processed: number;
        total_files: number;
        analyzed_edits?: number;
        total_edits?: number;
        chunks_completed?: number;
        total_chunks?: number;
      }
    | { type: 'job_created'; job_id: string }
    | { type: 'complete'; sections: Array<ExtractedSection>; slot_surface_forms?: SlotSurfaceForms }
    | { type: 'error'; message: string }
    | { type: 'heartbeat'; message?: string; elapsed_seconds?: number };

  // Carried across events so a content-bearing heartbeat (no rule/file counts of
  // its own) can still report a full ExtractionProgress -- it just repeats the
  // last known counts alongside the fresher message/elapsed time.
  let lastProgress: ExtractionProgress = {
    file: '',
    rules_extracted: 0,
    rules_from_file: 0,
    files_processed: 0,
    total_files: 0,
  };

  const handleEvent = (event: StreamEvent): ExtractionResult | undefined => {
    if (event.type === 'progress') {
      console.debug('[extractRulesStream] Progress:', {
        rules_extracted: event.rules_extracted,
        rules_from_file: event.rules_from_file,
        file: event.file,
        files_processed: event.files_processed,
      });
      // Merge rather than replace -- a raw "progress" event never carries
      // message/analyzed_edits/total_edits/elapsedSeconds, so a full
      // replacement would wipe those fields the instant the first rule is
      // found, even though a later status/heartbeat event set them.
      lastProgress = { ...lastProgress, ...event };
      onProgress(lastProgress);
    } else if (event.type === 'status') {
      console.debug('[extractRulesStream] Status:', event.message);
      lastProgress = {
        ...lastProgress,
        files_processed: event.files_processed,
        total_files: event.total_files,
        message: event.message,
        analyzed_edits: event.analyzed_edits ?? lastProgress.analyzed_edits,
        total_edits: event.total_edits ?? lastProgress.total_edits,
        chunks_completed: event.chunks_completed ?? lastProgress.chunks_completed,
        total_chunks: event.total_chunks ?? lastProgress.total_chunks,
      };
      onProgress(lastProgress);
    } else if (event.type === 'job_created') {
      console.debug('[extractRulesStream] Job created:', event.job_id);
      onJobCreated?.(event.job_id);
    } else if (event.type === 'complete') {
      console.debug('[extractRulesStream] Complete event received');
      return { sections: event.sections, slotSurfaceForms: event.slot_surface_forms ?? {} };
    } else if (event.type === 'error') {
      console.error('[extractRulesStream] Error:', event.message);
      throw new Error(event.message);
    } else if (event.type === 'heartbeat') {
      console.debug('[extractRulesStream] Heartbeat:', event.message, event.elapsed_seconds);
      lastProgress = {
        ...lastProgress,
        message: event.message ?? lastProgress.message,
        elapsedSeconds: event.elapsed_seconds,
      };
      onProgress(lastProgress);
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }
      const result = handleEvent(JSON.parse(line) as StreamEvent);
      if (result !== undefined) {
        return result;
      }
    }
  }

  // Flush any remaining data after stream closes
  if (buffer.trim()) {
    const result = handleEvent(JSON.parse(buffer) as StreamEvent);
    if (result !== undefined) {
      return result;
    }
  }

  throw new Error('Stream ended without a complete event');
}

export async function extractRulesFromPlaybookDocument(
  bbAxios: AxiosInstance,
  {
    file,
    contractType,
    playbookId,
  }: {
    file: File;
    contractType: string;
    playbookId?: string;
  }
): Promise<Array<ExtractedSection>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('contract_type', contractType);
  if (playbookId) {
    formData.append('playbook_id', playbookId);
  }
  const { data } = await bbAxios.post<{ docs: Array<ExtractedSection> }>(
    EXTRACT_RULES_FROM_PLAYBOOK_DOC_ENDPOINT,
    formData,
    { timeout: 600_000 }
  );
  return data.docs;
}

/**
 * Analyze the currently open Word document as a first-party paper template.
 * Streams rule extraction progress, identical to extractRulesFromContractsStream
 * but with paper_type forced to 'first_party' and a single file (the current doc).
 */
export async function analyzeTemplateDocument(
  authedFetch: AuthedFetch,
  {
    file,
    contractType,
    playbookId,
  }: {
    file: File;
    contractType: string;
    playbookId?: string;
  },
  onProgress: (progress: ExtractionProgress) => void,
  onJobCreated?: (jobId: string) => void
): Promise<ExtractionResult> {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('paper_type', 'first_party');
  formData.append('contract_type', contractType);
  if (playbookId) {
    formData.append('playbook_id', playbookId);
  }

  const response = await authedFetch(`${EXTRACT_RULES_ENDPOINT}/stream`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw createStreamHttpError('Template analysis failed', response);
  }
  if (!response.body) {
    throw new Error('No response body from stream');
  }

  const headerJobId = response.headers.get('X-Async-Job-Id');
  if (headerJobId) {
    onJobCreated?.(headerJobId);
  }

  type StreamEvent =
    | ({ type: 'progress' } & ExtractionProgress)
    | {
        type: 'status';
        message: string;
        files_processed: number;
        total_files: number;
        analyzed_edits?: number;
        total_edits?: number;
        chunks_completed?: number;
        total_chunks?: number;
      }
    | { type: 'job_created'; job_id: string }
    | { type: 'complete'; sections: Array<ExtractedSection>; slot_surface_forms?: SlotSurfaceForms }
    | { type: 'error'; message: string }
    | { type: 'heartbeat'; message?: string; elapsed_seconds?: number };

  // See extractRulesFromContractsStream -- same rationale for carrying counts
  // forward across status/heartbeat events that don't carry their own.
  let lastProgress: ExtractionProgress = {
    file: '',
    rules_extracted: 0,
    rules_from_file: 0,
    files_processed: 0,
    total_files: 0,
  };

  const handleEvent = (event: StreamEvent): ExtractionResult | undefined => {
    if (event.type === 'progress') {
      // Merge rather than replace -- see extractRulesFromContractsStream.
      lastProgress = { ...lastProgress, ...(event as ExtractionProgress) };
      onProgress(lastProgress);
    } else if (event.type === 'status') {
      lastProgress = {
        ...lastProgress,
        files_processed: event.files_processed,
        total_files: event.total_files,
        message: event.message,
        analyzed_edits: event.analyzed_edits ?? lastProgress.analyzed_edits,
        total_edits: event.total_edits ?? lastProgress.total_edits,
        chunks_completed: event.chunks_completed ?? lastProgress.chunks_completed,
        total_chunks: event.total_chunks ?? lastProgress.total_chunks,
      };
      onProgress(lastProgress);
    } else if (event.type === 'job_created') {
      onJobCreated?.(event.job_id);
    } else if (event.type === 'complete') {
      return { sections: event.sections, slotSurfaceForms: event.slot_surface_forms ?? {} };
    } else if (event.type === 'error') {
      throw new Error(event.message);
    } else if (event.type === 'heartbeat') {
      lastProgress = {
        ...lastProgress,
        message: event.message ?? lastProgress.message,
        elapsedSeconds: event.elapsed_seconds,
      };
      onProgress(lastProgress);
    }
  };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }
      const result = handleEvent(JSON.parse(line) as StreamEvent);
      if (result !== undefined) {
        return result;
      }
    }
  }

  if (buffer.trim()) {
    const result = handleEvent(JSON.parse(buffer) as StreamEvent);
    if (result !== undefined) {
      return result;
    }
  }

  throw new Error('Stream ended without a complete event');
}

// ---------------------------------------------------------------------------
// Save rules
// ---------------------------------------------------------------------------

export type SlotSurfaceForms = Record<string, Record<string, string>>;

export interface ExtractionResult {
  sections: Array<ExtractedSection>;
  slotSurfaceForms: SlotSurfaceForms;
}

export interface SaveRulesRequest {
  playbookId: string;
  playbookName: string;
  contractType: string;
  sections: Array<ExtractedSection>;
  paperType?: 'first_party' | 'third_party';
  slotSurfaceForms?: SlotSurfaceForms;
}

export interface SaveRulesResult {
  playbookId: string;
  jobId: string;
  rulesAdded: number;
  rulesSkipped: number;
  skippedRules: Array<string>;
}

export async function saveRules(
  bbAxios: AxiosInstance,
  payload: SaveRulesRequest
): Promise<SaveRulesResult> {
  const { data } = await bbAxios.post<{ docs: SaveRulesResult }>(SAVE_RULES_ENDPOINT, payload, {
    timeout: 120000,
  });
  return data.docs;
}

/**
 * Create an empty playbook immediately, before any rules are extracted.
 * This allows the chat assistant to be available as soon as the user reaches
 * the upload step (step 3).
 */
export async function createPlaybook(
  bbAxios: AxiosInstance,
  { name, contractType }: { name: string; contractType: string }
): Promise<{ playbookId: string }> {
  const result = await saveRules(bbAxios, {
    playbookId: '',
    playbookName: name,
    contractType,
    sections: [],
  });
  return { playbookId: result.playbookId };
}

// ---------------------------------------------------------------------------
// Clone playbook
// ---------------------------------------------------------------------------

export interface ClonePlaybookResult {
  playbookId: string;
  name: string;
  clonedRules: number;
  skippedRules: number;
}

export async function clonePlaybook(
  bbAxios: AxiosInstance,
  playbookId: string,
  name?: string
): Promise<ClonePlaybookResult> {
  const { data } = await bbAxios.post<{ docs: ClonePlaybookResult }>(CLONE_PLAYBOOK_ENDPOINT, {
    playbookId,
    name,
  });
  return data.docs;
}

// ---------------------------------------------------------------------------
// Search rules
// ---------------------------------------------------------------------------

export interface RuleSearchResult {
  ruleId: string;
  name: string;
  description: string;
  trainTypes: Array<string>;
  trainingCount: number;
  playbookId: string;
  playbookName: string;
  sampleSentences: Array<string>;
  sampleEdits?: Array<{ before: string; after: string }>;
}

export async function searchRules(
  bbAxios: AxiosInstance,
  query: string,
  excludeModel: string
): Promise<Array<RuleSearchResult>> {
  const { data } = await bbAxios.get<{ docs: { results: Array<RuleSearchResult> } }>(
    SEARCH_RULES_ENDPOINT,
    { params: { q: query, excludeModel } }
  );
  return data.docs.results;
}

// ---------------------------------------------------------------------------
// Match extracted rules (batch)
// ---------------------------------------------------------------------------

export async function matchExtractedRules(
  bbAxios: AxiosInstance,
  {
    playbookId,
    extractedSections,
    includeSamePlaybook = false,
  }: {
    playbookId: string;
    extractedSections: Array<ExtractedSection>;
    /** When true, also searches within the current playbook for existing rules to augment. */
    includeSamePlaybook?: boolean;
  }
): Promise<Map<string, Array<SuggestedMatch>>> {
  const allRules = extractedSections.flatMap((s) =>
    s.rules.map((r) => ({ id: r.id, name: r.name, description: r.description }))
  );

  if (allRules.length === 0) {
    return new Map();
  }

  const { data } = await bbAxios.post<{
    docs: {
      matches: Array<{
        extractedRuleId: string;
        matches: Array<{
          rule_id: string;
          name: string;
          description: string;
          playbook_id: string;
          playbook_name: string;
          confidence: number;
          sample_edit?: { before: string; after: string };
        }>;
      }>;
    };
  }>(
    MATCH_EXTRACTED_RULES_ENDPOINT,
    {
      playbook_id: playbookId,
      extracted_rules: allRules,
      include_same_playbook: includeSamePlaybook,
    },
    {
      timeout: 120000, // 2 minutes - embedding search can take 30+ seconds
    }
  );

  const matchMap = new Map<string, Array<SuggestedMatch>>();
  for (const item of data.docs?.matches ?? []) {
    matchMap.set(
      item.extractedRuleId,
      item.matches.map((m) => ({
        ruleId: m.rule_id,
        name: m.name,
        description: m.description,
        playbookId: m.playbook_id,
        playbookName: m.playbook_name,
        confidence: m.confidence,
        sampleEdit: m.sample_edit,
        samePlaybook: (m as { same_playbook?: boolean }).same_playbook ?? false,
      }))
    );
  }
  return matchMap;
}

// ---------------------------------------------------------------------------
// Remove / copy rule
// ---------------------------------------------------------------------------

export async function removeRuleFromPlaybook(
  bbAxios: AxiosInstance,
  { playbookId, ruleId }: { playbookId: string; ruleId: string }
): Promise<void> {
  await bbAxios.put('/api/v2/onboarding/remove-rule', { playbookId, ruleId });
}

export interface CopiedRule {
  ruleId: string;
  name: string;
  description: string;
}

export async function copyRuleToPlaybook(
  bbAxios: AxiosInstance,
  {
    sourceRuleId,
    playbookId,
    sectionName,
  }: { sourceRuleId: string; playbookId: string; sectionName?: string }
): Promise<CopiedRule> {
  const { data } = await bbAxios.post<{ docs: CopiedRule }>('/api/v2/onboarding/copy-rule', {
    sourceRuleId,
    playbookId,
    ...(sectionName ? { sectionName } : {}),
  });
  return data.docs;
}

// ---------------------------------------------------------------------------
// Onboarding chat
// ---------------------------------------------------------------------------

export interface ChatRuleCreated {
  ruleId: string;
  name: string;
  description?: string;
  ruleFamily?: string;
  alignmentMode?: string;
  tailSectionName?: string;
  contractType?: string;
  sampleEdit?: { before: string; after: string };
}

export interface ChatRuleSearchResult {
  ruleId: string;
  name: string;
  description: string;
  playbookId: string;
  playbookName: string;
  trainingCount: number;
  confidence?: number;
  sampleEdits?: Array<{ before: string; after: string }>;
  sectionFallback?: string;
}

export interface ChatSessionContext {
  skipped_rules?: Array<string>;
  [key: string]: unknown;
}

export interface OnboardingChatRequest {
  playbookId: string;
  contractType: string;
  message: string;
  sessionContext?: ChatSessionContext;
  chatHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

export interface OnboardingChatResult {
  reply: string;
  rulesCreated: Array<ChatRuleCreated>;
  rulesModified: Array<ChatRuleCreated>;
  searchResults: Array<ChatRuleSearchResult>;
  sessionContext: ChatSessionContext;
  chatHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
}

export async function onboardingChat(
  bbAxios: AxiosInstance,
  payload: OnboardingChatRequest
): Promise<OnboardingChatResult> {
  const { data } = await bbAxios.post<{ docs: OnboardingChatResult }>(
    '/api/v2/onboarding/ftue-chat',
    payload,
    { timeout: 60_000 }
  );
  return data.docs;
}

// ---------------------------------------------------------------------------
// Rule examples
// ---------------------------------------------------------------------------

export interface RuleExample {
  tuId: string;
  before: string;
  after: string;
  contextBefore?: string;
  contextFollowing?: string;
  trainType?: string;
}

export interface RuleExamplesResult {
  ruleId: string;
  ruleName: string;
  examples: Array<RuleExample>;
  totalCount: number;
}

export async function getRuleExamples(
  bbAxios: AxiosInstance,
  ruleId: string,
  offset = 0,
  limit = 3
): Promise<RuleExamplesResult> {
  const { data } = await bbAxios.get<{ docs: RuleExamplesResult }>(
    `/api/v2/onboarding/rule-examples/${ruleId}`,
    { params: { offset, limit }, timeout: 10_000 }
  );
  return data.docs;
}

export async function removeRuleExample(
  bbAxios: AxiosInstance,
  ruleId: string,
  tuId: string
): Promise<void> {
  await bbAxios.delete(`/api/v2/onboarding/rule-examples/${ruleId}/${tuId}`, {
    timeout: 10_000,
  });
}

/** Set the user's preferred (active) playbook. Pass null to clear. */
export async function updatePreferredModel(
  bbAxios: AxiosInstance,
  modelId: string | null
): Promise<void> {
  await bbAxios.patch('/api/current-user/preferred-model', { modelId });
}
